import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { 
  getAuth,
  GoogleAuthProvider, 
  FacebookAuthProvider, 
  GithubAuthProvider,
  connectAuthEmulator,
  type Auth
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';

// Firebase configuration
if (import.meta.env.DEV) {
  console.log('🔍 Firebase Config Debug:', {
    hasApiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
    apiKeyLength: import.meta.env.VITE_FIREBASE_API_KEY?.length || 0,
    apiKeyPreview: import.meta.env.VITE_FIREBASE_API_KEY 
      ? `${import.meta.env.VITE_FIREBASE_API_KEY.substring(0, 10)}...` 
      : 'NOT SET',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'NOT SET',
  });
}

const rawApiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const invalidPlaceholders = ['demo-api-key', 'your_api_key_here', ''];
const hasValidApiKey =
  typeof rawApiKey === 'string' &&
  rawApiKey.length > 20 &&
  !invalidPlaceholders.includes(rawApiKey);

if (!hasValidApiKey) {
  const msg =
    'Firebase API key is missing or invalid. Set VITE_FIREBASE_API_KEY in packages/web/.env.local (see env.example). Get the key from Firebase Console → Project settings → Your apps → Web app.';
  console.error('❌', msg);
  if (typeof window !== 'undefined') {
    console.error('❌ This will cause "auth/api-key-not-valid" when signing in, signing out, or on the login screen.');
  }
}

const firebaseConfig = {
  apiKey: rawApiKey || "demo-api-key",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "courtsync-local.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "courtsync-local",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "courtsync-local.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abcdef123456"
};

// Initialize Firebase app
let app: FirebaseApp;
const existingApps = getApps();
if (existingApps.length > 0) {
  app = existingApps[0];
} else {
  app = initializeApp(firebaseConfig);
}

// Initialize Firestore and Storage first (needed for emulator check)
const isDevelopment = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_PROD !== 'true';
const databaseId = isDevelopment ? undefined : 'courtsync-db';
const db: Firestore = databaseId ? getFirestore(app, databaseId) : getFirestore(app);
const storage: FirebaseStorage = getStorage(app);

// Initialize Firebase Authentication
export const auth: Auth = getAuth(app);

if (isDevelopment) {
  console.log('🔧 Development mode: Connecting to Firebase emulators (must be before any auth use)');
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('✅ Firebase emulators connected successfully');
  } catch (error) {
    console.error('❌ Firebase emulators not available. Start them with: npm run dev:firebase', error);
  }
} else {
  console.log('🚀 Using Firebase production services');
}

export { db, storage };

// Social Auth Providers
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const githubProvider = new GithubAuthProvider();

googleProvider.addScope('email');
googleProvider.addScope('profile');
facebookProvider.addScope('email');
facebookProvider.addScope('public_profile');
githubProvider.addScope('user:email');

export default app;
